<template>
  <div v-if="isEditing && isAnchorOnly" class="my-5 h-5">
    <div class="bk-anchor-indicator container">
      <div class="flex flex-wrap items-center gap-3 pt-2">
        <div><SpriteSymbol name="anchor" class="size-4" /></div>
        <div class="font-bold">{{ anchorTitle || title }}</div>
        <div class="text-grey-dark">Wird nur in blökkli angezeigt</div>
      </div>
    </div>
  </div>
  <div
    v-else-if="!isAnchorOnly"
    :id="anchorId"
    class="paragraph-title"
    :class="{
      'container mb-spacing-base mt-spacing-lg': !parentType,
      'opacity-20': isEditing && isAnchorOnly,
      'max-w-screen-desktop': parentType,
    }"
  >
    <h2 :id="anchorHeadingId" class="title is-h3 relative text-balance">
      <span v-blokkli-editable:field_title>{{ title }}</span>
    </h2>

    <p
      v-if="subtitle"
      v-blokkli-editable:field_text
      class="subtitle is-h4 text-balance"
    >
      {{ subtitle }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import type { ParagraphTitleFragment } from '#graphql-operations'
import { toValidHtmlId } from '~/helpers'

const props = defineProps<ParagraphTitleFragment>()

const isEditing = false

const { parentType, options, uuid } = defineBlokkli({
  bundle: 'title',
  globalOptions: ['showAnchor', 'bkHiddenGlobally', 'publicationChannels'],
  options: {
    anchorOnly: {
      type: 'checkbox',
      label: 'Nur in Anker anzeigen',
      default: false,
      group: 'Advanced',
    },
  },
  editor: {
    previewWidth: 800,
    determineVisibleOptions(ctx) {
      if (ctx.options.showAnchor) {
        return [
          'showAnchor',
          'anchorOnly',
          'bkHiddenGlobally',
          'publicationChannels',
        ]
      }
      return ['showAnchor', 'bkHiddenGlobally', 'publicationChannels']
    },
  },
})

const anchorId = computed<string | undefined>(() => {
  if (!options.value.showAnchor) {
    return undefined
  }

  const id = props.anchorTitle || props.title || uuid
  return toValidHtmlId(id)
})

const anchorHeadingId = computed<string | undefined>(() => {
  if (!props.title) {
    return undefined
  }

  const id = toValidHtmlId(props.title)

  // Prevent generating and applying the same ID twice.
  if (id === anchorId.value) {
    return undefined
  }

  return id
})

const isAnchorOnly = computed(
  () => options.value.showAnchor && options.value.anchorOnly,
)
</script>

<style lang="postcss">
.toggle-container {
  > .paragraph-title {
    @apply !mb-spacing-base !mt-spacing-lg;
  }
}
</style>
